
import type { PaletteOptions } from "@mui/material/styles";
import type { ClientTheme } from "./types";
import { createElement } from "react";
import {
  PerformanceZoneIcon,
  OperationalZoneIcon,
  CollaborationZoneIcon,
  ReportingZoneIcon,
  RiskZoneIcon,
} from "../assets/broadhamicons";

// ─── Cadent assets ────────────────────────────────────────────────────────────
import cadentLightLogo   from "../assets/cadent-light.png";
import cadentDarkLogo    from "../assets/cadent-dark.png";
import cadentLoginBg     from "../assets/bg.webp";

// ─── Broadham Green assets ────────────────────────────────────────────────────
import broadhamLightLogo from "../assets/broadham-light-transparent.png";
import broadhamDarkLogo  from "../assets/broadham-dark-transparent.png";
import broadhamLoginBg   from "../assets/bg2.webp";

// ─── Broadham Green colour tokens (also used by the sidebar layout config) ────
export const NAVY       = "#1B2D5B";
export const GREEN      = "#27AE60";
export const GREEN_DARK = "#1E8449";
export const TEAL       = "#00897B";

// ─── Central CLIENTS registry ─────────────────────────────────────────────────
export const CLIENTS: Record<string, ClientTheme> = {
  CADENT: {
    key: "CADENT",
    title: "Cadent Capital Delivery",
    titleAbbreviation: "CCD",
    platformName: "Project & Portfolio Platform",
    logos: {
      light: cadentLightLogo,
      dark:  cadentDarkLogo,
    },
    loginBackground: cadentLoginBg,
    favicon: "/cadent-favicon.ico",
    logoMaxWidth: "112px",
    logoMaxHeight: "30px",
    palette: {
      light: {
        mode: "light",
        primary: { main: "#E84B0F" },
      },
      dark: {
        mode: "dark",
        primary: { main: "#F8863F" },
      },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      button: { textTransform: "uppercase" },
    },
  },

  BROADHAM_GREEN: {
    key: "BROADHAM_GREEN",
    title: "Capital Delivery",
    titleAbbreviation: "CD",
    platformName: "Water & Portfolio Platform",
    logos: {
      light: broadhamLightLogo,
      dark:  broadhamDarkLogo,
    },
    loginBackground: broadhamLoginBg,
    favicon: "/broadham-green-favicon.svg",
    logoMaxWidth: "112px",
    logoMaxHeight: "50px",
    palette: {
      light: {
        mode: "light",
        primary: { main: GREEN, dark: GREEN_DARK, contrastText: "#FFFFFF" },
      },
      dark: {
        mode: "dark",
        primary: { main: "#11f683", dark: GREEN, contrastText: "#FFFFFF" },
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", sans-serif',
      h1: { fontWeight: 700 },
      h2: { fontWeight: 700 },
      h3: { fontWeight: 600 },
      h4: { fontWeight: 600 },
      button: { fontWeight: 600, textTransform: "uppercase" },
    },
    layout: {
      sidebarBackground: NAVY,
      sidebarTextPrimary: "#FFFFFF",
      sidebarTextSecondary: "rgba(255, 255, 255, 0.80)",
      sidebarActiveItemBg: "#2D52D6",
      sidebarLogoBoxBg: "#FFFFFF",
      sidebarLogoBoxText: NAVY,
      appBackground: "#FFFFFF",
      dashboardItemVariant: "plain",
      zoneIcons: {
        "Performance Optimisation Zone": createElement(PerformanceZoneIcon),
        "Operational Delivery Zone": createElement(OperationalZoneIcon),
        "Collaboration Zone": createElement(CollaborationZoneIcon),
        "Reporting Zone": createElement(ReportingZoneIcon),
        "Risk & Compliance Controller": createElement(RiskZoneIcon),
      },
    },
  },
};

const DEFAULT_CLIENT_KEY = "CADENT";

export const getClientKey = (): string => {
  return String(import.meta.env.VITE_APP_BRAND ?? DEFAULT_CLIENT_KEY);
};

export const getResolvedClientKey = (): keyof typeof CLIENTS => {
  const normalizedClientKey = getClientKey().toUpperCase().replace(/-/g, "_");

  return CLIENTS[normalizedClientKey]
    ? (normalizedClientKey as keyof typeof CLIENTS)
    : DEFAULT_CLIENT_KEY;
};

export const getClientTheme = (): ClientTheme => {
  return CLIENTS[getResolvedClientKey()];
};

export const getClientPalette = (
  mode: "light" | "dark"
): Partial<PaletteOptions> => {
  const client = getClientTheme();
  return client.palette?.[mode] ?? ({ mode } as Partial<PaletteOptions>);
};

export const getClientFavicon = (): string => {
  const client = getClientTheme();
  return client.favicon ?? "/favicon.ico";
};
